<template>
  <v-dialog v-model="dialog" width="600px" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card :tile="$vuetify.breakpoint.xsOnly" class="d-flex flex-column">
      <v-card-title class="teal white--text px-3">
        <span class="headline">Health Check</span>
        <v-spacer />
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon dark @click="reload()" class="mr-2"><v-icon>sync</v-icon></v-btn>
        <v-btn v-else elevation="0" color="orange" dark @click="reload()" class="mr-2">
          <v-icon class="mr-2">sync</v-icon>
          Atualizar
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="columns"
        :items="services"
        sort-by="role"
        sort-desc
        disable-pagination
        fixed-header
        hide-default-footer
        class="elevation-0"
        light
        :loading="loading"
        no-data-text="Não há informações disponíveis! Aguarde alguns minutos e tente novamente."
      >
        <template v-slot:item.state="props">
          <v-tooltip top attach>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" :color="props.item.state === 'running' ? (props.item.healthy === 'healthy' ? 'success' : 'warning') : 'error'">
                {{ props.item.state === 'running' ? 'bolt' : 'power_off' }}
              </v-icon>
            </template>
            <span>{{ props.item.status }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  v-if="!scanned || !item.cve"
                  icon
                  disabled>
                  <v-icon>hourglass_top</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  icon
                  :disabled="!(item.cve.high + item.cve.critical)"
                  :color="item.cve.critical > 0 ? 'error' : 'warning'"
                  @click="cve(item.service, item.cve)">
                  <v-icon>heart_broken</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{ cveTooltip(item.cve) }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <template v-if="services.length > 0 || releaser">
        <v-divider />
        <v-card-text class="px-4 py-3 body-2 grey--text text--darken-2" style="background-color: #fafafa;">
          <div v-if="releaser" class="d-flex align-center mb-1" :class="releaser.stale ? 'warning--text text--darken-2' : ''">
            <v-icon small class="mr-2" :color="releaser.stale ? 'warning darken-2' : ''">dns</v-icon>
            <span>{{ releaserText }}</span>
          </div>
          <div v-if="date.trim() !== ''" class="d-flex align-center">
            <v-icon small class="mr-2">sync</v-icon>
            <span>Status verificado em <b>{{ date }}</b>.</span>
          </div>
          <template v-if="scanned">
            <div class="d-flex align-center mt-1">
              <v-icon small class="mr-2">bug_report</v-icon>
              <span>CVEs verificados em <b>{{ cveSummary.date }}</b> ({{ cveSummary.critical }} critical e {{ cveSummary.high }} high).</span>
            </div>
            <div v-if="cveSummary.critical + cveSummary.high > 0" class="d-flex align-center mt-1">
              <v-icon small class="mr-2">build</v-icon>
              <span>{{ fixableText }}</span>
            </div>
          </template>
          <div v-else class="d-flex align-center mt-1">
            <v-icon small class="mr-2">hourglass_top</v-icon>
            <span>CVEs em análise... aguarde!</span>
          </div>
        </v-card-text>
      </template>

      <v-alert :type="this.error ? 'error' : 'success'" :value="error || success" transition="scale-transition" class="mx-4 mt-3">
        {{ message }}
      </v-alert>

      <!-- Build implantada por um Releaser (servidor da equipe): reiniciar e desativar são do deployer da plataforma e não se aplicam. -->
      <v-card-actions class="pb-4 pt-3">
        <v-row wrap>
          <v-col cols="6" class="pr-1">
            <v-btn x-large block :dark="isAdmin && !releaser && !loading" color="blue-grey" :disabled="!isAdmin || !!releaser || loading" @click="restart()" elevation="0">
              <v-icon class="mr-2">restart_alt</v-icon>
              Reiniciar
            </v-btn>
          </v-col>
          <v-col cols="6" class="pl-1">
            <v-btn x-large block color="error" :disabled="!isAdmin || !!releaser || loading" @click="undeploy()" elevation="0">
              <v-icon class="mr-2">cloud_off</v-icon>
              Desativar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
      <v-card-actions v-if="$vuetify.breakpoint.xsOnly">
        <v-row wrap>
          <v-col cols="12">
            <v-btn
              color="error"
              @click="dialog = false"
              outlined
              block
            >
              Fechar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
    <confirm-wrapper ref="confirm" />
    <dialog-wrapper ref="info" />
    <dialog-vulnerabilities ref="cve" />
  </v-dialog>
</template>

<script>
import axios from 'axios'

import EmbrapaHelper from '@/helpers/embrapa'
import ErrorHelper from '@/helpers/error'

import ConfirmWrapper from '@/components/ConfirmDialog.vue'
import DialogWrapper from '@/components/DialogDefault.vue'
import DialogVulnerabilities from '@/components/DialogVulnerabilities.vue'

export default {
  mixins: [
    EmbrapaHelper,
    ErrorHelper
  ],
  components: {
    ConfirmWrapper,
    DialogWrapper,
    DialogVulnerabilities
  },
  data: () => ({
    dialog: false,
    user: null,
    project: {
      maintainer: false
    },
    app: null,
    stage: null,
    services: [],
    date: '',
    cveSummary: { date: '', critical: 0, high: 0, fixable: 0 },
    // Build implantada por um Releaser: as ações do deployer (reiniciar, desativar) não se aplicam.
    releaser: null,
    columns: [
      { text: 'Serviço', align: 'start', value: 'service' },
      { text: 'Criado Em', value: 'created', sortable: false },
      { text: 'Status', align: 'center', value: 'state', sortable: false },
      { text: 'Tamanho', value: 'size', sortable: false },
      { text: '', value: 'actions', sortable: false }
    ],
    headers: {},
    error: false,
    success: false,
    message: '',
    loading: false
  }),
  beforeMount () {
    this.user = this.$localStorage.get('user')
  },
  mounted () {
    if (this.user.authenticated) {
      this.headers = {
        Authorization: 'Bearer ' + this.$localStorage.get('user').token
      }
    }
  },
  methods: {
    open (project, app, stage) {
      this.project = project
      this.app = app
      this.stage = stage

      this.reload()

      this.dialog = true
    },
    reload () {
      this.loading = true

      this.error = false
      this.success = false
      this.message = ''

      this.services = []
      this.date = ''
      this.cveSummary = { date: '', critical: 0, high: 0, fixable: 0 }
      this.releaser = null

      const error = error => {
        this.error = true
        this.message = this.errorMessage(error)

        this.loading = false
      }

      axios.get(process.env.VUE_APP_API + '/build/health/' + this.app.repository + '/' + this.stage, { headers: this.headers }).then(response => {
        this.services = response.data.services
        this.date = response.data.date
        this.cveSummary = response.data.cve || { date: '', critical: 0, high: 0, fixable: 0 }
        this.releaser = response.data.releaser || null

        this.loading = false
      }).catch(error)
    },
    undeploy () {
      this.$refs.confirm.open(
        'Desativar instância?',
        'A execução da build "' + this.app.repository + '@' + this.stage + '" (instanciada no servidor "' + this.app.stages[this.stage].cluster + '") será ENCERRADA! Com isso, todos os containers da build serão suspensos (e a aplicação ficará OFFLINE), mas continuarão no servidor (podendo ser reativados a qualquer momento). Nenhum dado será perdido.',
        { color: 'error', width: 500, persistent: true }).then((confirm) => {
        if (confirm) {
          this.loading = true

          this.error = false
          this.success = false
          this.message = ''

          axios.post(process.env.VUE_APP_API + '/build/undeploy/' + this.app.repository + '/' + this.stage, {}, { headers: this.headers }).then(response => {
            this.loading = false

            this.$emit('refresh')

            this.dialog = false
          }).catch(error => {
            this.error = true
            this.message = this.errorMessage(error)

            this.loading = false
          })
        }
      })
    },
    restart () {
      this.$refs.confirm.open(
        'Reiniciar a instância?',
        'A execução da build "' + this.app.repository + '@' + this.stage + '" (instanciada no servidor "' + this.app.stages[this.stage].cluster + '") será REINICIADA!',
        { color: 'error', width: 500, persistent: true }).then((confirm) => {
        if (confirm) {
          this.loading = true

          this.error = false
          this.success = false
          this.message = ''

          axios.post(process.env.VUE_APP_API + '/build/restart/' + this.app.repository + '/' + this.stage, {}, { headers: this.headers }).then(response => {
            this.success = true
            this.message = 'Reinício agendado... agora é só aguardar!'

            this.loading = false
          }).catch(error => {
            this.error = true
            this.message = this.errorMessage(error)

            this.loading = false
          })
        }
      })
    },
    cve (service, cve) {
      this.$refs.cve.open(this.app.repository, this.stage, service, cve.vulnerabilities, this.cveSummary.date)
    },
    cveTooltip (cve) {
      if (!this.scanned || !cve) return 'CVEs em análise... aguarde!'

      if (!(cve.high + cve.critical)) return 'CVEs: nenhum CRITICAL ou HIGH'

      return 'CVEs: ' + cve.critical + ' CRITICAL e ' + cve.high + ' HIGH (' + cve.fixable + ' com correção disponível)'
    }
  },
  computed: {
    isAdmin () {
      return this.$localStorage.get('user').role === 'Administrator'
    },
    scanned () {
      return typeof this.cveSummary.date === 'string' && this.cveSummary.date.trim() !== ''
    },
    releaserText () {
      const r = this.releaser

      if (!r) return ''

      const seen = r.seen ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }).format(new Date(r.seen)) : '?'

      const where = 'Releaser em ' + r.server + (r.version ? ' (versão ' + r.version + ')' : '')

      return r.stale
        ? 'Sem contato com o ' + where + ' desde ' + seen + '.'
        : 'Implantado pelo ' + where + '. Último contato em ' + seen + '.'
    },
    fixableText () {
      const n = this.cveSummary.fixable

      if (n === 0) return 'Nenhum dos CVEs tem correção disponível.'

      if (n === 1) return 'Existe 1 CVE com correção disponível.'

      return 'Existem ' + n + ' CVEs com correção disponível.'
    }
  }
}
</script>
